"use client";

import Image from "next/image";
import { useState } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const NAMESPACE = "new-sls-selective-laser-sintering";

const IMAGE_BASE = "/image/new-version/more/SLS/Diverse-Industrial-Applications";

const CATEGORIES = [
	{ titleKey: "applicationsCategory1Title", descKey: "applicationsCategory1Desc" },
	{ titleKey: "applicationsCategory2Title", descKey: "applicationsCategory2Desc" },
	{ titleKey: "applicationsCategory3Title", descKey: "applicationsCategory3Desc" },
	{ titleKey: "applicationsCategory4Title", descKey: "applicationsCategory4Desc" },
	{ titleKey: "applicationsCategory5Title", descKey: "applicationsCategory5Desc" },
	{ titleKey: "applicationsCategory6Title", descKey: "applicationsCategory6Desc" },
] as const;

const IMAGES = [
	{ src: `${IMAGE_BASE}/r&d.webp`, labelKey: "applicationsImageLabel1", href: null },
	{ src: `${IMAGE_BASE}/robotics.webp`, labelKey: "applicationsImageLabel2", href: "/industry/robotics-automation" },
	{ src: `${IMAGE_BASE}/automotive.webp`, labelKey: "applicationsImageLabel3", href: "/industry/automotive" },
	{ src: `${IMAGE_BASE}/medical-devices.webp`, labelKey: "applicationsImageLabel4", href: "/industry/medical-device" },
	{ src: `${IMAGE_BASE}/consumer-products.webp`, labelKey: "applicationsImageLabel5", href: "/industry/consumer-goods" },
	{ src: `${IMAGE_BASE}/industrial-tooling.webp`, labelKey: "applicationsImageLabel6", href: "/industry/industry-equipment" },
] as const;

export default function SlsApplications() {
	const t = useTranslations(NAMESPACE);
	const [activeLabelKey, setActiveLabelKey] = useState<string | null>(null);

	return (
		<section className="bg-[#ffffff] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<span className="mb-3 inline-block text-xl uppercase tracking-wider text-themePrimary400 max-md:text-lg">
						{t("applicationsEyebrow")}
					</span>
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("applicationsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("applicationsDesc")}</p>
				</div>

				<div className="grid items-start gap-8 lg:grid-cols-[6fr_4fr] lg:gap-10 xl:gap-14">
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2">
						{CATEGORIES.map((cat) => (
							<div key={cat.titleKey}>
								<h3 className="mb-2 text-base font-semibold text-themePrimary400 sm:text-lg">
									{t(cat.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(cat.descKey)}</p>
							</div>
						))}
					</div>

					<div className="grid grid-cols-2 gap-3 self-start">
						{IMAGES.map((image, index) => (
							image.href ? (
								<Link
									key={image.labelKey}
									href={image.href}
									onClick={() => setActiveLabelKey(image.labelKey)}
									className={`group flex flex-col rounded-xl p-4 transition-colors duration-300 ${
										activeLabelKey === image.labelKey
											? "bg-[#88d43c]"
											: "bg-[#ededed] hover:bg-[#88d43c]"
									}`}
								>
									<div className="relative h-16 w-16 overflow-hidden rounded-full sm:h-20 sm:w-20">
										<Image src={image.src} alt={t(image.labelKey)} fill className="object-cover" sizes="80px" />
									</div>
									<span
										className={`mt-4 self-end text-right text-sm font-semibold sm:text-base ${
											activeLabelKey === image.labelKey
												? "text-white"
												: "text-[#1f1f1f] group-hover:text-white"
										}`}
									>
										{t(image.labelKey)}
									</span>
								</Link>
							) : (
								<button
									key={image.labelKey}
									type="button"
									onClick={() => setActiveLabelKey(image.labelKey)}
									aria-pressed={activeLabelKey === image.labelKey}
									className={`group flex flex-col rounded-xl p-4 text-left transition-colors duration-300 ${
										activeLabelKey === image.labelKey
											? "bg-[#88d43c]"
											: "bg-[#ededed] hover:bg-[#88d43c]"
									}`}
								>
									<div className="relative h-16 w-16 overflow-hidden rounded-full sm:h-20 sm:w-20">
										<Image src={image.src} alt={t(image.labelKey)} fill className="object-cover" sizes="80px" />
									</div>
									<span
										className={`mt-4 self-end text-right text-sm font-semibold sm:text-base ${
											activeLabelKey === image.labelKey
												? "text-white"
												: "text-[#1f1f1f] group-hover:text-white"
										}`}
									>
										{t(image.labelKey)}
									</span>
								</button>
							)
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
